import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    BaseEdge,
    Controls,
    ControlButton,
    getBezierPath,
    Position,
    useNodesState,
    useEdgesState,
    useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Icon from "./icon";
import {useTranslate} from "../useTranslate";
import {ORPHAN_GOALS_SECTION_ID, ORPHAN_PROJECTS_SECTION_ID} from "../../pages/goals/tabs/strategy-map-graph";
import {
    getStrategyStatusColor,
    normalizeStrategyStatus,
    STRATEGY_STATUS,
} from "../strategy-map-status";

const NODE_WIDTH = 320;
const MISSION_NODE_HEIGHT = 176;
const GOAL_NODE_HEIGHT = 272;
const PROJECT_NODE_HEIGHT = 224;
const ORPHAN_SECTION_NODE_WIDTH = 240;
const ORPHAN_SECTION_NODE_HEIGHT = 54;
const NODE_ACTION_GUTTER_X = 120;
const NODE_ACTION_GUTTER_Y = 96;
const STRATEGY_MAP_ROOT_TOP_Y = 132;
const TREE_SAFE_PADDING_X = 80;
const MISSION_TO_GOAL_GAP_Y = 100;
const GOAL_TO_CHILD_GAP_Y = 90;
const PROJECT_GAP_X = 40;
const PROJECT_ROW_GAP_Y = 48;
const BRANCH_GAP_X = 72;
const ORPHAN_SECTION_GAP_Y = 180;
const ORPHAN_TITLE_TO_GRID_GAP_Y = 36;

const NODE_DIMENSIONS = {
    mission: {width: NODE_WIDTH, height: MISSION_NODE_HEIGHT},
    goal: {width: NODE_WIDTH, height: GOAL_NODE_HEIGHT},
    project: {width: NODE_WIDTH, height: PROJECT_NODE_HEIGHT},
    orphanSection: {width: ORPHAN_SECTION_NODE_WIDTH, height: ORPHAN_SECTION_NODE_HEIGHT},
    default: {width: NODE_WIDTH, height: PROJECT_NODE_HEIGHT},
};

const NODE_LAYOUT_DIMENSIONS = {
    mission: {
        width: NODE_WIDTH + NODE_ACTION_GUTTER_X,
        height: MISSION_NODE_HEIGHT + NODE_ACTION_GUTTER_Y,
    },
    goal: {
        width: NODE_WIDTH + NODE_ACTION_GUTTER_X,
        height: GOAL_NODE_HEIGHT + NODE_ACTION_GUTTER_Y,
    },
    project: {
        width: NODE_WIDTH + NODE_ACTION_GUTTER_X,
        height: PROJECT_NODE_HEIGHT + NODE_ACTION_GUTTER_Y,
    },
    orphanSection: {width: ORPHAN_SECTION_NODE_WIDTH, height: ORPHAN_SECTION_NODE_HEIGHT},
    default: {
        width: NODE_WIDTH,
        height: PROJECT_NODE_HEIGHT,
    },
};

const VIEWPORT_SAFE_LEFT_X = 48;
const VIEWPORT_SAFE_RIGHT_X = 48;
const VIEWPORT_TOP_SAFE_AREA = 124;
const MIN_READABLE_ZOOM = 0.35;
const MAX_INITIAL_ZOOM = 1;

let elkPromise;

function getElk() {
    if (!elkPromise) {
        elkPromise = import("elkjs/lib/elk.bundled.js")
            .then(module => {
                const elkClass = module?.default?.default || module?.default || module;

                return new elkClass();
            })
            .catch(error => {
                elkPromise = undefined;
                throw error;
            });
    }

    return elkPromise;
}

function getStatusColor(item) {
    return getStrategyStatusColor(
        item?.type === "goal"
            ? item?.entity?.goalStatus
            : item?.entity?.projectStatus
    );
}

export function canParentHaveChildType(parentType, childType) {
    if (!parentType || !childType) return false;
    if (parentType === "mission") return childType === "goal";
    if (parentType === "goal") return childType === "goal" || childType === "project";
    if (parentType === "project") return childType === "project";
    return false;
}

function buildEdge(parentId, childId, childItem, dashed = false, options = {}) {
    const color = getStatusColor(childItem);
    const normalizedStatus = normalizeStrategyStatus(
        childItem?.type === "goal"
            ? childItem?.entity?.goalStatus
            : childItem?.entity?.projectStatus
    );
    const isRiskOrError = [
        STRATEGY_STATUS.AT_RISK,
        STRATEGY_STATUS.OFF_TRACK,
    ].includes(normalizedStatus);

    return {
        id: `${dashed ? "e-extra" : "e-main"}-${parentId}-${childId}`,
        source: String(parentId),
        target: String(childId),
        sourceHandle: "bottom",
        targetHandle: "top",
        type: "StrategyMapEdge",
        markerEnd: undefined,
        layout: !dashed,
        data: {
            layoutPoints: [],
            ...(options.synthetic ? {synthetic: true} : {}),
        },
        style: {
            stroke: color,
            strokeWidth: isRiskOrError ? 3 : 2.5,
            strokeDasharray: "6 4",
            opacity: isRiskOrError ? 1 : 0.85,
        },
        entity: childItem.entity,
    };
}

function StrategyMapEdge({
    id,
    data,
    markerEnd,
    style,
    sourceX,
    sourceY,
    sourcePosition = Position.Bottom,
    targetX,
    targetY,
    targetPosition = Position.Top,
}) {
    const layoutPoints = data?.layoutPoints || [];
    const edgePath = layoutPoints.length > 1
        ? layoutPoints.reduce((path, point, index) => (
            `${path}${index === 0 ? "M" : " L"} ${point.x} ${point.y}`
        ), "")
        : getBezierPath({
            sourceX,
            sourceY,
            sourcePosition,
            targetX,
            targetY,
            targetPosition,
            curvature: 0.4,
        })[0];

    return (
        <BaseEdge
            id={id}
            path={edgePath}
            markerEnd={markerEnd}
            style={{strokeDasharray: "6 4", ...style}}
        />
    );
}

const STRATEGY_MAP_EDGE_TYPES = {StrategyMapEdge};

const MISSION_DIRECT_CHILD_LIMIT = 3;

// The mission is allowed at most MISSION_DIRECT_CHILD_LIMIT direct child goals.
// When the data links more goals straight to the mission, the extras are moved one
// level down and distributed under the kept children (which stay directly under the
// mission). This keeps the mission as the single root and the tree balanced without
// ever introducing a fake root.
function enforceMissionDirectGoalLimit(edges, nodeById, missionRootIds) {
    if (!missionRootIds.length) return;

    const missionId = missionRootIds[0];
    const directChildEdges = edges.filter(edge => edge.source === missionId);

    if (directChildEdges.length <= MISSION_DIRECT_CHILD_LIMIT) return;

    const entityValue = (value) => String(
        value?.order ?? value?.createdAt ?? value?.id ?? ""
    );
    const sorted = [...directChildEdges]
        .map(edge => ({edge, value: entityValue(nodeById[edge.target]?.data?.entity)}))
        .sort((a, b) => a.value.localeCompare(b.value, undefined, {
            numeric: true,
            sensitivity: "base",
        }));
    const keptIds = sorted
        .slice(0, MISSION_DIRECT_CHILD_LIMIT)
        .map(({edge}) => edge.target);

    sorted.slice(MISSION_DIRECT_CHILD_LIMIT).forEach(({edge}, index) => {
        const anchorId = keptIds[index % MISSION_DIRECT_CHILD_LIMIT];

        edge.source = anchorId;
        edge.id = `e-main-${anchorId}-${edge.target}`;
    });
}

export function buildGraph(items = []) {
    const nodes = items.map((it) => ({
        id: String(it.id),
        type: it.type,
        position: it.position || {x: 0, y: 0},
        data: {
            entity: it.entity || {},
            section: it.section,
        },
        origin: [0, 0],
        draggable: it.draggable !== false,
    }));

    const nodeIds = new Set(nodes.map(node => node.id));
    const missionIds = new Set(
        nodes.filter(node => node.type === "mission").map(node => node.id)
    );
    const itemById = items.reduce((acc, it) => ({
        ...acc,
        [String(it.id)]: it,
    }), {});
    const edges = [];

    const addEdge = (parentId, childId, childItem, dashed = false, options = {}) => {
        const sourceId = String(parentId);
        const targetId = String(childId);

        // Rule 4 (no self-loop) and rule 1 (nothing can be a parent of the mission).
        if (sourceId === targetId) return;
        if (missionIds.has(targetId)) return;

        edges.push(buildEdge(sourceId, targetId, childItem, dashed, options));
    };

    for (const it of items) {
        if (it.parentId && nodeIds.has(String(it.parentId)) && nodeIds.has(String(it.id))) {
            addEdge(it.parentId, it.id, it, false, it.synthetic ? {synthetic: true} : {});
        }

        for (const parentId of it.parentIds || []) {
            if (!parentId || parentId === it.parentId) continue;
            if (!nodeIds.has(String(parentId)) || !nodeIds.has(String(it.id))) continue;
            addEdge(parentId, it.id, it, true);
        }
    }

    // ---- mainTree / orphanProjects split (BFS from the mission roots) ----
    // The mission is the only root and the only node in the top layer. Any goal or
    // project that is NOT reachable from a mission root through valid edges is moved
    // out of the main tree. Only projects may be orphaned; goals are force-attached
    // to the mission with a synthetic edge so they always stay in the main tree.
    const missionRootIds = [...missionIds].filter(
        id => !edges.some(edge => edge.target === id)
    );
    const reachableRoots = missionRootIds.length ? missionRootIds : [...missionIds];
    const reachable = new Set();
    const stack = [...reachableRoots];

    while (stack.length) {
        const nodeId = stack.pop();

        if (reachable.has(nodeId)) continue;

        reachable.add(nodeId);
        for (const edge of edges) {
            if (edge.source === nodeId && !reachable.has(edge.target)) {
                stack.push(edge.target);
            }
        }
    }

    // Goals without a parent (not attached to the mission) and everything below them live in their own
    // "goals without parent" section; their edges are not drawn.
    const orphanGoalIds = new Set(
        nodes.filter(node => node.type === "goal" && node.data.section === "orphan-goals").map(node => node.id)
    );
    let orphanGoalsGrew = orphanGoalIds.size > 0;

    while (orphanGoalsGrew) {
        orphanGoalsGrew = false;
        for (const edge of edges) {
            if (orphanGoalIds.has(edge.source) && !orphanGoalIds.has(edge.target) && itemById[edge.target]?.type === "goal") {
                orphanGoalIds.add(edge.target);
                orphanGoalsGrew = true;
            }
        }
    }
    for (const node of nodes) {
        if (orphanGoalIds.has(node.id)) node.data.section = "orphan-goals";
    }

    const orphanProjectIds = new Set();

    for (const node of nodes) {
        if (node.type !== "project" || reachable.has(node.id)) continue;

        node.data.section = "orphan-projects";
        orphanProjectIds.add(node.id);
    }

    // Any unreachable goal gets a synthetic mission edge so the main tree keeps it
    // (rule 2); orphan projects stay edge-less inside their separate section.
    for (const node of nodes) {
        if (node.type !== "goal" || reachable.has(node.id)) continue;

        const missionId = reachableRoots[0];

        if (!missionId || orphanProjectIds.has(node.id) || orphanGoalIds.has(node.id)) continue;

        addEdge(missionId, node.id, itemById[node.id] || node, false, {synthetic: true});
        reachable.add(node.id);
    }

    // Rule: the mission keeps at most MISSION_DIRECT_CHILD_LIMIT direct goal
    // children; extra goals move one level down under the kept children.
    const nodeById = nodes.reduce((acc, node) => ({
        ...acc,
        [node.id]: node,
    }), {});
    enforceMissionDirectGoalLimit(edges, nodeById, reachableRoots);

    const finalEdges = edges.filter(edge => (
        !orphanProjectIds.has(edge.source) && !orphanProjectIds.has(edge.target)
        && !orphanGoalIds.has(edge.source) && !orphanGoalIds.has(edge.target)
    ));

    if (orphanGoalIds.size) {
        nodes.push({
            id: ORPHAN_GOALS_SECTION_ID,
            type: "orphanSection",
            position: {x: 0, y: 0},
            data: {
                entity: {
                    id: ORPHAN_GOALS_SECTION_ID,
                    titleKey: "strategy-map.orphan-goals.title",
                },
                section: "orphan-goals",
            },
            origin: [0, 0],
            draggable: false,
        });
    }

    if (orphanProjectIds.size) {
        nodes.push({
            id: ORPHAN_PROJECTS_SECTION_ID,
            type: "orphanSection",
            position: {x: 0, y: 0},
            data: {
                entity: {
                    id: ORPHAN_PROJECTS_SECTION_ID,
                    titleKey: "strategy-map.orphan-projects.title",
                },
                section: "orphan-projects",
            },
            origin: [0, 0],
            draggable: false,
        });
    }

    return {nodes, edges: finalEdges};
}

function collectDescendantIds(nodeId, edges) {
    const childrenByParent = edges.reduce((acc, edge) => {
        acc[edge.source] = [...(acc[edge.source] || []), edge.target];
        return acc;
    }, {});

    const descendants = [];

    const visit = (id) => {
        for (const childId of childrenByParent[id] || []) {
            if (!descendants.includes(childId)) {
                descendants.push(childId);
                visit(childId);
            }
        }
    };

    visit(String(nodeId));
    return descendants;
}

export function getNodeDimensions(node, withActionGutter = false) {
    const dimensionsMap = withActionGutter ? NODE_LAYOUT_DIMENSIONS : NODE_DIMENSIONS;
    const measuredWidth = Number(node?.measured?.width || node?.width);
    const measuredHeight = Number(node?.measured?.height || node?.height);

    if (!withActionGutter && measuredWidth > 0 && measuredHeight > 0) {
        return {
            width: measuredWidth,
            height: measuredHeight,
        };
    }

    return dimensionsMap[node?.type] || dimensionsMap.default;
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function stableNodeSort(a, b) {
    const getValue = (node) => (
        node.data?.entity?.order ??
        node.data?.entity?.createdAt ??
        node.data?.entity?.id ??
        node.id
    );

    return String(getValue(a)).localeCompare(String(getValue(b)), undefined, {
        numeric: true,
        sensitivity: "base",
    });
}

function getProjectColumns(availableBranchWidth, projectCount) {
    if (!projectCount) return 0;

    const projectWidth = NODE_DIMENSIONS.project.width;
    const columns = Math.floor(
        (availableBranchWidth + PROJECT_GAP_X) / (projectWidth + PROJECT_GAP_X)
    );

    return clamp(columns || 1, 1, Math.min(3, projectCount));
}

function getGridSize(count, columns, nodeType) {
    if (!count || !columns) return {width: 0, height: 0, rows: 0};

    const dimensions = getNodeDimensions({type: nodeType});
    const rows = Math.ceil(count / columns);

    return {
        width: (columns * dimensions.width) + ((columns - 1) * PROJECT_GAP_X),
        height: (rows * dimensions.height) + ((rows - 1) * PROJECT_ROW_GAP_Y),
        rows,
    };
}

function computeSubtreeWidth(node, childrenByParent, nodeById) {
    const layoutDimensions = getNodeDimensions(node, true);
    const children = (childrenByParent[node.id] || [])
        .map(childId => nodeById[childId])
        .filter(Boolean)
        .sort(stableNodeSort);
    const childrenRowWidth = children.length
        ? children.reduce((total, child) => {
            return total + computeSubtreeWidth(child, childrenByParent, nodeById);
        }, 0) + ((children.length - 1) * BRANCH_GAP_X)
        : 0;

    return Math.max(layoutDimensions.width, childrenRowWidth, NODE_WIDTH);
}

function placeSubtree(node, left, top, positions, childrenByParent, nodeById) {
    const nodeDimensions = getNodeDimensions(node);
    const layoutDimensions = getNodeDimensions(node, true);
    const children = (childrenByParent[node.id] || [])
        .map(childId => nodeById[childId])
        .filter(Boolean)
        .sort(stableNodeSort);

    positions[node.id] = {
        x: left + (layoutDimensions.width / 2) - (nodeDimensions.width / 2),
        y: top + ((layoutDimensions.height - nodeDimensions.height) / 2),
    };

    if (!children.length) return;

    const childWidths = children.map(child => computeSubtreeWidth(child, childrenByParent, nodeById));
    const childrenRowWidth = childWidths.reduce((total, width) => total + width, 0)
        + ((children.length - 1) * BRANCH_GAP_X);
    let childLeft = left + (layoutDimensions.width / 2) - (childrenRowWidth / 2);
    const childTop = top + layoutDimensions.height + GOAL_TO_CHILD_GAP_Y;

    children.forEach((child, index) => {
        placeSubtree(child, childLeft, childTop, positions, childrenByParent, nodeById);
        childLeft += childWidths[index] + BRANCH_GAP_X;
    });
}

function getNodeBounds(node) {
    const dimensions = getNodeDimensions(node);

    return {
        x: node.position.x,
        y: node.position.y,
        width: dimensions.width,
        height: dimensions.height,
        minX: node.position.x,
        minY: node.position.y,
        maxX: node.position.x + dimensions.width,
        maxY: node.position.y + dimensions.height,
    };
}

function getNodesBounds(nodes) {
    if (!nodes.length) {
        return {
            x: 0,
            y: STRATEGY_MAP_ROOT_TOP_Y,
            width: 0,
            height: 0,
            minX: 0,
            minY: STRATEGY_MAP_ROOT_TOP_Y,
            maxX: 0,
            maxY: STRATEGY_MAP_ROOT_TOP_Y,
        };
    }

    const bounds = nodes.reduce((nextBounds, node) => {
        const nodeBounds = getNodeBounds(node);

        return {
            minX: Math.min(nextBounds.minX, nodeBounds.minX),
            minY: Math.min(nextBounds.minY, nodeBounds.minY),
            maxX: Math.max(nextBounds.maxX, nodeBounds.maxX),
            maxY: Math.max(nextBounds.maxY, nodeBounds.maxY),
        };
    }, {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity,
    });

    return {
        x: bounds.minX,
        y: bounds.minY,
        width: bounds.maxX - bounds.minX,
        height: bounds.maxY - bounds.minY,
        ...bounds,
    };
}

function isOrphanMember(node) {
    return (node.type === "project" && node.data?.section === "orphan-projects")
        || (node.type === "goal" && node.data?.section === "orphan-goals");
}

/**
 * Places the "goals without parent" and "projects without goal" sections one under the other, each as a title
 * node followed by a grid of cards, centred on `centerX` starting at `topY`.
 */
function layoutOrphanGroups({sectionNodes, goalNodes, projectNodes, centerX, topY, width}) {
    const result = [];
    let y = topY;
    const groups = [
        ["orphan-goals", goalNodes, "goal"],
        ["orphan-projects", projectNodes, "project"],
    ];

    for (const [section, members, type] of groups) {
        const header = sectionNodes.find(node => node.data?.section === section);
        if (!members.length && !header) continue;

        if (header) {
            result.push({
                ...header,
                position: {x: centerX - (getNodeDimensions(header).width / 2), y},
            });
        }
        const gridY = y + ORPHAN_SECTION_NODE_HEIGHT + ORPHAN_TITLE_TO_GRID_GAP_Y;
        const dimensions = getNodeDimensions({type});
        const columns = getProjectColumns(Math.max(width, dimensions.width), members.length) || 1;
        const grid = getGridSize(members.length, columns, type);
        const gridX = centerX - (grid.width / 2);

        members.forEach((node, index) => {
            result.push({
                ...node,
                position: {
                    x: gridX + ((index % columns) * (dimensions.width + PROJECT_GAP_X)),
                    y: gridY + (Math.floor(index / columns) * (dimensions.height + PROJECT_ROW_GAP_Y)),
                },
            });
        });
        y = gridY + (grid.height || 0) + ORPHAN_SECTION_GAP_Y;
    }

    return result;
}

function getMainTreeNodes(nodes) {
    return nodes.filter(node => (
        node.type !== "orphanSection" && !isOrphanMember(node)
    ));
}

function getMainTreeBounds(nodes) {
    return getNodesBounds(getMainTreeNodes(nodes));
}

function getTopAlignedTreeViewport(nodes, containerSize) {
    const mainTreeNodes = getMainTreeNodes(nodes);
    const missionNode = mainTreeNodes.find(node => node.type === "mission");

    if (!mainTreeNodes.length || !missionNode || !containerSize.width) return undefined;

    const treeBounds = getNodesBounds(mainTreeNodes);
    const missionBounds = getNodeBounds(missionNode);
    const leftSafeArea = VIEWPORT_SAFE_LEFT_X;
    const rightSafeArea = VIEWPORT_SAFE_RIGHT_X;
    const topSafeArea = VIEWPORT_TOP_SAFE_AREA;
    const availableWidth = Math.max(1, containerSize.width - leftSafeArea - rightSafeArea);
    const widthBasedZoom = availableWidth / Math.max(treeBounds.width, 1);
    const zoom = clamp(widthBasedZoom, MIN_READABLE_ZOOM, MAX_INITIAL_ZOOM);
    const treeCenterX = treeBounds.x + (treeBounds.width / 2);
    const viewportCenterX = leftSafeArea + (availableWidth / 2);

    return {
        x: viewportCenterX - (treeCenterX * zoom),
        y: topSafeArea - (missionBounds.y * zoom),
        zoom,
    };
}

function getLayoutedElements(nodes, edges, options = {}) {
    const rootCenterX = options.rootCenterX || 720;
    const containerWidth = Math.max(options.containerWidth || rootCenterX * 2, NODE_WIDTH + (TREE_SAFE_PADDING_X * 2));
    const orphanSectionNodes = nodes.filter(node => node.type === "orphanSection");
    const orphanProjectNodes = nodes.filter(
        node => node.type === "project" && node.data?.section === "orphan-projects"
    );
    const orphanGoalNodes = nodes.filter(
        node => node.type === "goal" && node.data?.section === "orphan-goals"
    );
    const orphanNodeIds = new Set([
        ...orphanSectionNodes.map(node => node.id),
        ...orphanProjectNodes.map(node => node.id),
        ...orphanGoalNodes.map(node => node.id),
    ]);
    const layoutNodes = nodes.filter(node => !orphanNodeIds.has(node.id));
    const nodeById = layoutNodes.reduce((acc, node) => ({
        ...acc,
        [node.id]: node,
    }), {});
    const childrenByParent = edges.reduce((acc, edge) => {
        if (edge.layout === false) return acc;
        if (orphanNodeIds.has(edge.source) || orphanNodeIds.has(edge.target)) return acc;
        if (!nodeById[edge.source] || !nodeById[edge.target]) return acc;

        return {
            ...acc,
            [edge.source]: [...(acc[edge.source] || []), edge.target],
        };
    }, {});
    const missionNode = layoutNodes.find(node => node.type === "mission");
    const positions = {};

    if (missionNode) {
        const missionDimensions = getNodeDimensions(missionNode);
        const missionLayoutDimensions = getNodeDimensions(missionNode, true);
        const rootChildren = (childrenByParent[missionNode.id] || [])
            .map(childId => nodeById[childId])
            .filter(Boolean)
            .sort(stableNodeSort);
        const rootWidths = rootChildren.map(child => computeSubtreeWidth(child, childrenByParent, nodeById));
        const rootRowWidth = rootWidths.reduce((total, width) => total + width, 0)
            + ((rootWidths.length - 1) * BRANCH_GAP_X);
        const missionX = rootCenterX - (missionDimensions.width / 2);
        const missionTop = STRATEGY_MAP_ROOT_TOP_Y;

        positions[missionNode.id] = {
            x: missionX,
            y: missionTop,
        };

        if (rootChildren.length) {
            const branchTop = missionTop + missionLayoutDimensions.height + MISSION_TO_GOAL_GAP_Y;
            let branchLeft = rootCenterX - (rootRowWidth / 2);

            rootChildren.forEach((child, index) => {
                placeSubtree(child, branchLeft, branchTop, positions, childrenByParent, nodeById);
                branchLeft += rootWidths[index] + BRANCH_GAP_X;
            });
        }
    }

    const mainLayout = layoutNodes
        .filter(node => positions[node.id])
        .map(node => ({
            ...node,
            position: positions[node.id],
        }));
    const unplacedNodes = layoutNodes.filter(node => !positions[node.id]);
    const layoutBounds = getMainTreeBounds(mainLayout);
    const projectDimensions = getNodeDimensions({type: "project"});
    const sectionWidth = Math.max(layoutBounds.width, projectDimensions.width);
    const baseX = layoutBounds.x + (layoutBounds.width / 2);
    const unplacedLayout = [];
    let sectionTop = layoutBounds.maxY + ORPHAN_SECTION_GAP_Y;

    if (unplacedNodes.length) {
        const unplacedWidths = unplacedNodes.map(node => (
            getNodeDimensions(node).width + PROJECT_GAP_X
        ));
        const unplacedRowWidth = unplacedWidths.reduce((total, width) => total + width, 0) - PROJECT_GAP_X;
        let unplacedLeft = baseX - (unplacedRowWidth / 2);

        unplacedNodes.forEach((node, index) => {
            unplacedLayout.push({
                ...node,
                position: {
                    x: unplacedLeft,
                    y: sectionTop,
                },
            });
            unplacedLeft += unplacedWidths[index];
        });

        sectionTop += Math.max(
            ...unplacedNodes.map(node => getNodeDimensions(node).height)
        ) + ORPHAN_SECTION_GAP_Y;
    }

    const orphanLayout = layoutOrphanGroups({
        sectionNodes: orphanSectionNodes,
        goalNodes: orphanGoalNodes,
        projectNodes: orphanProjectNodes,
        centerX: baseX,
        topY: sectionTop,
        width: sectionWidth,
    });

    return [
        ...mainLayout,
        ...unplacedLayout,
        ...orphanLayout,
    ];
}

export function autoLayoutNodes(nodes, edges, options) {
    return getLayoutedElements(nodes, edges, options);
}

function getGraphSignature(nodes, edges) {
    const nodePart = nodes.map(node => node.id).sort().join("|");
    const edgePart = edges
        .map(edge => `${edge.source}>${edge.target}:${edge.layout === false ? "extra" : "main"}`)
        .sort()
        .join("|");

    return `${nodePart}::${edgePart}`;
}

function getElkEdgePoints(edge) {
    const section = edge?.sections?.[0];

    if (!section?.startPoint || !section?.endPoint) return [];

    return [
        section.startPoint,
        ...(section.bendPoints || []),
        section.endPoint,
    ];
}

export async function layoutStrategyGraph(nodes, edges, options = {}) {
    const elk = await getElk();
    const containerWidth = Math.max(options.containerWidth || 1440, NODE_WIDTH + (TREE_SAFE_PADDING_X * 2));
    const orphanNodes = nodes.filter(node => (
        node.type === "orphanSection" || isOrphanMember(node)
    ));
    const orphanIds = new Set(orphanNodes.map(node => node.id));
    const mainNodes = nodes.filter(node => !orphanIds.has(node.id));
    const mainNodeIds = new Set(mainNodes.map(node => node.id));
    const layoutEdges = edges.filter(edge => (
        mainNodeIds.has(edge.source) && mainNodeIds.has(edge.target)
    ));
    const result = await elk.layout({
        id: "strategy-map-root",
        layoutOptions: {
            "elk.algorithm": "layered",
            "elk.direction": "DOWN",
            "elk.edgeRouting": "ORTHOGONAL",
            "elk.padding": `[top=${TREE_SAFE_PADDING_X},left=${TREE_SAFE_PADDING_X},bottom=${TREE_SAFE_PADDING_X},right=${TREE_SAFE_PADDING_X}]`,
            "elk.spacing.nodeNode": String(BRANCH_GAP_X),
            "elk.spacing.edgeNode": "32",
            "elk.layered.spacing.nodeNodeBetweenLayers": String(GOAL_TO_CHILD_GAP_Y),
            "elk.layered.spacing.edgeNodeBetweenLayers": "36",
            "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
            "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
            "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
            "elk.layered.cycleBreaking.strategy": "GREEDY",
        },
        children: mainNodes.map(node => {
            const dimensions = getNodeDimensions(node);

            return {
                id: node.id,
                width: dimensions.width,
                height: dimensions.height,
                layoutOptions: node.type === "mission"
                    ? {"elk.layered.layering.layerConstraint": "FIRST"}
                    : undefined,
            };
        }),
        edges: layoutEdges.map(edge => ({
            id: edge.id,
            sources: [edge.source],
            targets: [edge.target],
        })),
    });
    const elkNodeById = new Map((result.children || []).map(node => [node.id, node]));
    const resultNodeYs = (result.children || []).map(node => Number(node.y) || 0);
    const firstMainY = resultNodeYs.length ? Math.min(...resultNodeYs) : 0;
    const graphWidth = Number(result.width) || containerWidth;
    const shiftX = Math.max(TREE_SAFE_PADDING_X, (containerWidth - graphWidth) / 2);
    const shiftY = STRATEGY_MAP_ROOT_TOP_Y - firstMainY;
    const positionedMainNodes = [];
    const unplacedNodes = [];

    for (const node of mainNodes) {
        const layoutNode = elkNodeById.get(node.id);

        if (!layoutNode) {
            unplacedNodes.push(node);
            continue;
        }

        positionedMainNodes.push({
            ...node,
            position: {
                x: (Number(layoutNode.x) || 0) + shiftX,
                y: (Number(layoutNode.y) || 0) + shiftY,
            },
        });
    }

    if (unplacedNodes.length) {
        const fallbackTop = resultNodeYs.length
            ? (Math.max(...resultNodeYs) + shiftY) + GOAL_TO_CHILD_GAP_Y
            : STRATEGY_MAP_ROOT_TOP_Y + GOAL_TO_CHILD_GAP_Y;
        let fallbackLeft = shiftX + TREE_SAFE_PADDING_X;

        for (const node of unplacedNodes) {
            const dimensions = getNodeDimensions(node);

            positionedMainNodes.push({
                ...node,
                position: {
                    x: fallbackLeft,
                    y: fallbackTop,
                },
            });
            fallbackLeft += dimensions.width + BRANCH_GAP_X;
        }
    }
    const elkEdgeById = new Map((result.edges || []).map(edge => [edge.id, edge]));
    const routedEdges = edges.map(edge => {
        const points = getElkEdgePoints(elkEdgeById.get(edge.id)).map(point => ({
            x: (Number(point.x) || 0) + shiftX,
            y: (Number(point.y) || 0) + shiftY,
        }));

        return {
            ...edge,
            data: {
                ...(edge.data || {}),
                layoutPoints: points,
            },
        };
    });

    if (!orphanNodes.length) {
        return {nodes: positionedMainNodes, edges: routedEdges};
    }

    const mainBounds = getNodesBounds(positionedMainNodes);
    const orphanSectionNodes = orphanNodes.filter(node => node.type === "orphanSection");
    const positionedOrphans = layoutOrphanGroups({
        sectionNodes: orphanSectionNodes,
        goalNodes: orphanNodes.filter(node => node.type === "goal"),
        projectNodes: orphanNodes.filter(node => node.type === "project"),
        centerX: mainBounds.x + (mainBounds.width / 2),
        topY: mainBounds.maxY + ORPHAN_SECTION_GAP_Y,
        width: Math.max(mainBounds.width, NODE_WIDTH),
    });

    return {
        nodes: [...positionedMainNodes, ...positionedOrphans],
        edges: routedEdges,
    };
}

function Flow({items, onChange, onRequestDialog, onNodePositionChange, nodeTypes, onEdgesDelete, onEdgeAction, layoutRevision, layoutMode = "base", readOnly = false, lockNodeDrag = false}) {
    const translate = useTranslate();
    const reactFlow = useReactFlow();
    const {screenToFlowPosition} = reactFlow;
    const containerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({width: 0, height: 0});
    const measuredContainerWidth = containerSize.width || 1440;
    const {nodes: ns, edges: es} = useMemo(() => buildGraph(items), [items]);
    const [nodes, setNodes, onNodesChange] = useNodesState(ns);
    const [edges, setEdges, onEdgesChange] = useEdgesState(es);
    const initialFitAppliedRef = useRef(false);
    const dimensionsByNodeRef = useRef(new Map());
    const baseLayoutCacheRef = useRef(undefined);
    const layoutRequestRef = useRef(0);
    const aliveRef = useRef(true);
    const [dimensionRevision, setDimensionRevision] = useState(0);

    useEffect(() => {
        aliveRef.current = true;

        return () => {
            aliveRef.current = false;
        };
    }, []);

    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        const updateSize = () => {
            const {width, height} = container.getBoundingClientRect();

            if (width > 0 && height > 0) {
                setContainerSize(previousSize => (
                    previousSize.width === width && previousSize.height === height
                        ? previousSize
                        : {width, height}
                ));
            }
        };

        updateSize();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateSize);

            return () => window.removeEventListener("resize", updateSize);
        }

        const observer = new ResizeObserver(updateSize);
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    const subtreeDragRef = useRef(null);
    const setTreeViewport = useCallback((targetNodes = [], options = {}) => {
        const viewport = getTopAlignedTreeViewport(targetNodes, containerSize);

        if (!viewport) return;

        reactFlow.setViewport(viewport, {
            duration: options.duration ?? 500,
        });
    }, [containerSize, reactFlow]);

    const setTreeViewportAfterPaint = useCallback((targetNodes = [], options = {}) => {
        requestAnimationFrame(() => {
            setTreeViewport(targetNodes, options);
        });
    }, [setTreeViewport]);

    const applyNodeInteractivity = useCallback((nextNodes) => nextNodes.map(node => ({
        ...node,
        draggable: !readOnly && !lockNodeDrag && node.draggable !== false,
    })), [readOnly, lockNodeDrag]);

    const runLayout = useCallback(async ({force = false, fit = false} = {}) => {
        if (!ns.length) {
            setNodes([]);
            setEdges([]);
            return;
        }

        const dimensionAwareNodes = ns.map(node => {
            const dimensions = dimensionsByNodeRef.current.get(node.id);

            return dimensions ? {...node, measured: dimensions} : node;
        });
        const dimensionsSignature = dimensionAwareNodes
            .map(node => {
                const dimensions = getNodeDimensions(node);
                return `${node.id}:${dimensions.width}x${dimensions.height}`;
            })
            .sort()
            .join("|");
        const signature = `${getGraphSignature(dimensionAwareNodes, es)}::${dimensionsSignature}`;
        const cachedLayout = baseLayoutCacheRef.current;

        if (!force && layoutMode === "base" && cachedLayout?.signature === signature && cachedLayout?.revision === layoutRevision) {
            const cachedNodesById = new Map(cachedLayout.nodes.map(node => [node.id, node]));
            const cachedEdgesById = new Map(cachedLayout.edges.map(edge => [edge.id, edge]));
            const restoredNodes = applyNodeInteractivity(dimensionAwareNodes.map(node => ({
                ...node,
                position: cachedNodesById.get(node.id)?.position || node.position,
            })));
            const restoredEdges = es.map(edge => ({
                ...edge,
                data: cachedEdgesById.get(edge.id)?.data || edge.data,
            }));

            setNodes(restoredNodes);
            setEdges(restoredEdges);
            if (fit) setTreeViewportAfterPaint(restoredNodes, {duration: 0});
            return;
        }

        const requestId = ++layoutRequestRef.current;
        let layout;

        try {
            layout = await layoutStrategyGraph(dimensionAwareNodes, es, {
                containerWidth: measuredContainerWidth,
            });
        }
        catch (error) {
            console.error(
                "[strategy-map] ELK layout failed; falling back to manual layout",
                {nodeCount: dimensionAwareNodes.length, edgeCount: es.length},
                error,
            );
            layout = {
                nodes: autoLayoutNodes(dimensionAwareNodes, es, {
                    rootCenterX: measuredContainerWidth / 2,
                    containerWidth: measuredContainerWidth,
                }),
                edges: es,
            };
        }

        if (!aliveRef.current || requestId !== layoutRequestRef.current) return;

        const nextNodes = applyNodeInteractivity(layout.nodes);
        setNodes(nextNodes);
        setEdges(layout.edges);

        if (layoutMode === "base") {
            baseLayoutCacheRef.current = {
                signature,
                revision: layoutRevision,
                nodes: nextNodes.map(node => ({id: node.id, position: node.position})),
                edges: layout.edges.map(edge => ({id: edge.id, data: edge.data})),
            };
        }

        if (fit) {
            setTreeViewportAfterPaint(nextNodes, {
                duration: initialFitAppliedRef.current ? 400 : 0,
            });
        }
    }, [
        ns,
        es,
        layoutMode,
        layoutRevision,
        measuredContainerWidth,
        applyNodeInteractivity,
        setEdges,
        setNodes,
        setTreeViewportAfterPaint,
    ]);

    useEffect(() => {
        const shouldFit = !initialFitAppliedRef.current && containerSize.width > 0;

        runLayout({fit: shouldFit}).then(() => {
            if (shouldFit) initialFitAppliedRef.current = true;
        });
    }, [runLayout, dimensionRevision, containerSize.width]);

    const handleNodesChange = useCallback((changes) => {
        let dimensionsChanged = false;

        for (const change of changes) {
            if (change.type !== "dimensions" || !change.dimensions) continue;

            const id = String(change.id);
            const previous = dimensionsByNodeRef.current.get(id);
            const next = {
                width: Number(change.dimensions.width),
                height: Number(change.dimensions.height),
            };

            if (previous?.width === next.width && previous?.height === next.height) continue;

            dimensionsByNodeRef.current.set(id, next);
            dimensionsChanged = true;
        }

        onNodesChange(changes);
        if (dimensionsChanged) setDimensionRevision(revision => revision + 1);
    }, [onNodesChange]);

    const handleAutoLayout = () => {
        baseLayoutCacheRef.current = undefined;
        runLayout({force: true, fit: true});
    };


    return (
        <div ref={containerRef} className="w-full h-full flex-1">
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={STRATEGY_MAP_EDGE_TYPES}
                onNodesChange={handleNodesChange}
                onEdgesChange={onEdgesChange}
                onEdgesDelete={readOnly ? undefined : onEdgesDelete}
                onEdgeClick={(event, edge) => {
                    if (readOnly) return;
                    event.stopPropagation();
                    onEdgeAction?.({
                        mode: "disconnect",
                        edge,
                    });
                }}
                nodesDraggable={!readOnly && !lockNodeDrag}
                panOnDrag
                nodesConnectable={!readOnly}
                edgesReconnectable={!readOnly}
                deleteKeyCode={readOnly ? null : ["Backspace", "Delete"]}
                onConnectEnd={(event, state) => {
                    if (readOnly) return;
                    if (!state?.fromNode) return;

                    if (!state?.isValid) {
                        onRequestDialog?.({
                            mode: "create-child",
                            attachTo: state.fromNode.id,
                            position: screenToFlowPosition({
                                x: event.clientX,
                                y: event.clientY,
                            }),
                            from: {
                                node: state.fromNode,
                                edge: state.fromPosition,
                            },
                        });
                        return;
                    }

                    onChange?.({
                        mode: "connect",
                        from: {
                            node: state.fromNode,
                            edge: state.fromPosition,
                        },
                        to: {
                            node: state.toNode,
                            edge: state.toPosition,
                        },
                    });
                }}
                onNodeClick={(event, node) => {
                    event.stopPropagation();
                    onRequestDialog?.({
                        mode: "edit",
                        id: node.id,
                        data: node.data,
                        type: node.type,
                    });
                }}
                onNodeDragStart={(_, node) => {
                    if (readOnly || lockNodeDrag) return;
                    const descendantIds = collectDescendantIds(node.id, edges);

                    subtreeDragRef.current = {
                        rootId: String(node.id),
                        offsets: nodes
                            .filter(item => descendantIds.includes(item.id))
                            .reduce((acc, item) => ({
                                ...acc,
                                [item.id]: {
                                    x: item.position.x - node.position.x,
                                    y: item.position.y - node.position.y,
                                },
                            }), {}),
                    };
                }}
                onNodeDrag={(_, node) => {
                    if (readOnly || lockNodeDrag) return;
                    const dragState = subtreeDragRef.current;

                    if (!dragState || dragState.rootId !== String(node.id)) return;

                    setNodes(currentNodes => currentNodes.map(item => {
                        const offset = dragState.offsets[item.id];

                        if (!offset) return item;

                        return {
                            ...item,
                            position: {
                                x: node.position.x + offset.x,
                                y: node.position.y + offset.y,
                            },
                        };
                    }));
                }}
                onNodeDragStop={(_, node) => {
                    if (readOnly || lockNodeDrag) return;
                    const dragState = subtreeDragRef.current;

                    const movedNodes = nodes
                        .filter(item => item.id === String(node.id) || dragState?.offsets?.[item.id])
                        .map(item => {
                            const offset = dragState?.offsets?.[item.id];

                            return {
                                ...item,
                                position: offset
                                    ? {
                                        x: node.position.x + offset.x,
                                        y: node.position.y + offset.y,
                                    }
                                    : {
                                        x: node.position.x,
                                        y: node.position.y,
                                    },
                            };
                        });

                    subtreeDragRef.current = null;

                    onNodePositionChange?.(
                        String(node.id),
                        {x: node.position.x, y: node.position.y},
                        node.data?.entity,
                        node.type,
                        movedNodes
                    );
                }}
                onDrop={(event) => {
                    if (readOnly) return;
                    const type = event.dataTransfer.getData("application/reactflow");

                    if (type) {
                        onRequestDialog?.({
                            mode: "create-root",
                            position: screenToFlowPosition({
                                x: event.clientX,
                                y: event.clientY,
                            }),
                            draft: {type},
                        });
                    }
                }}
                onDragOver={(event) => {
                    if (!readOnly) event.preventDefault();
                }}
                zoomOnDoubleClick={false}
                minZoom={0.35}
                maxZoom={1.6}
                defaultEdgeOptions={{
                    type: "StrategyMapEdge",
                }}
            >
                <Background color="#D1D5DB" gap={28}/>
                <Controls position="bottom-left" style={{bottom: "1rem"}}>
                    <ControlButton onClick={handleAutoLayout} title={translate("strategy-map.action.auto-layout")}>
                        <Icon icon="mi mi:account_tree" className="text-base"/>
                    </ControlButton>
                </Controls>
            </ReactFlow>
        </div>
    );
}

export default function ZofinoGraph(props) {
    return (
        <ReactFlowProvider>
            <Flow {...props}/>
        </ReactFlowProvider>
    );
}




// import React, { useEffect, useMemo, useRef } from "react";
// import {
//     ReactFlow,
//     ReactFlowProvider,
//     Background,
//     Controls,
//     useNodesState,
//     useEdgesState,
//     useReactFlow,
//     Handle,
//     Position,
//     MarkerType,
// } from "@xyflow/react";
// import "@xyflow/react/dist/style.css";
//
// import { ProgressBar } from "primereact/progressbar";
// import { useTranslate } from "../useTranslate";
//
// /* ---------------- helpers ---------------- */
//
// function buildGraph(items) {
//     const nodes = items?.map((it) => ({
//         id: String(it.id),
//         type: it.type,
//         position: it.position,
//         data: { entity: it.entity || {} },
//         origin: [0.5, 0],
//         draggable: it.draggable !== false,
//     }));
//
//     const edges = [];
//
//     for (const it of items) {
//         if (it.parentId) {
//             edges.push({
//                 id: `e-main-${it.parentId}-${it.id}`,
//                 source: String(it.parentId),
//                 target: String(it.id),
//                 markerEnd: { type: MarkerType.ArrowClosed },
//                 entity: it.entity
//             });
//         }
//     }
//
//     for (const it of items) {
//         for (const p of it.parentIds || []) {
//             edges.push({
//                 id: `e-extra-${p}-${it.id}`,
//                 source: String(p),
//                 target: String(it.id),
//                 markerEnd: { type: MarkerType.ArrowClosed },
//                 style: { opacity: 0.7 },
//                 entity: it.entity,
//             });
//         }
//     }
//
//     return { nodes, edges };
// }
//
// function collectDescendantIds(nodeId, edges) {
//     const childrenByParent = edges.reduce((acc, edge) => {
//         acc[edge.source] = [...(acc[edge.source] || []), edge.target];
//         return acc;
//     }, {});
//
//     const descendants = [];
//
//     const visit = (id) => {
//         for (const childId of childrenByParent[id] || []) {
//             if (!descendants.includes(childId)) {
//                 descendants.push(childId);
//                 visit(childId);
//             }
//         }
//     };
//
//     visit(String(nodeId));
//     return descendants;
// }
//
// // function NodeCard({ type, entity }) {
// //     const t = useTranslate();
// //     const color = { objective: "bg-primary-500", project: "bg-purple-500", task: "bg-green-500" }[type];
// //
// //     return (
// //         <div className="bg-white shadow-1 border-round-lg text-xs">
// //             <div className={`p-2 text-white font-bold ${color}`}>{t(type)}</div>
// //             <div className="p-2">{entity.title}</div>
// //             {entity.owner && <div className="px-2 text-600">مسئول: {entity.owner}</div>}
// //             {typeof entity.progress === "number" && <ProgressBar value={entity.progress} className="m-2" />}
// //         </div>
// //     );
// // }
// //
// // const BaseNode = ({ type, data }) => (
// //     <>
// //         <Handle id="top" type="target" position={Position.Top} isConnectableStart isConnectableEnd />
// //         <NodeCard type={type} entity={data.entity} />
// //         <Handle id="bottom" type="source" position={Position.Bottom} isConnectableStart isConnectableEnd />
// //     </>
// // );
//
// function Flow({ items, onChange, onRequestDialog, onNodePositionChange, nodeTypes, onEdgesDelete }) {
//     const { screenToFlowPosition } = useReactFlow();
//     const { nodes: ns, edges: es } = useMemo(() => buildGraph(items), [items]);
//
//     const [nodes, setNodes, onNodesChange] = useNodesState(ns);
//     const [edges, setEdges, onEdgesChange] = useEdgesState(es);
//
//     useEffect(() => {
//         setNodes(ns);
//         setEdges(es);
//     }, [ns, es, setNodes, setEdges]);
//
//     const startRef = useRef({});
//     const subtreeDragRef = useRef(null);
//
//     return (
//         <div className="w-full h-full flex-1">
//             <ReactFlow
//                 nodes={nodes}
//                 edges={edges}
//                 nodeTypes={nodeTypes}
//                 onNodesChange={onNodesChange}
//                 onEdgesChange={onEdgesChange}
//                 onEdgesDelete={onEdgesDelete}
//                 onConnectStart={(_, p) => (startRef.current = p)}
//                 // onConnect={(p) => {
//                 //     onChange?.({ type: "connect", payload: { start: startRef.current, params: p } })
//                 // }}
//                 onConnectEnd={(e, s) => {
//                     if (!s?.isValid) {
//                         onRequestDialog?.({
//                             mode: "create-child",
//                             attachTo: s.fromNode.id,
//                             position: screenToFlowPosition({ x: e.clientX, y: e.clientY }),
//                             from:{node:s.fromNode, edge:s.fromPosition}
//                         });
//                     }
//                     else {
//                         onChange?.(
//                             {
//                                 mode: "connect",
//                                 from:{node:s.fromNode, edge:s.fromPosition},
//                                 to:{node:s.toNode, edge:s.toPosition},
//                             }
//                         )
//                     }
//                 }}
//                 onNodeClick={(_, n) => {
//                     // _.preventDefault();
//                     // console.log(n)
//                     onRequestDialog?.({ mode: "edit", id: n.id, data:n.data, type:n.type})
//                 }}
//                 onNodeDragStart={(_, n) => {
//                     const descendantIds = collectDescendantIds(n.id, edges);
//
//                     subtreeDragRef.current = {
//                         rootId: String(n.id),
//                         offsets: nodes
//                             .filter(node => descendantIds.includes(node.id))
//                             .reduce((acc, node) => ({
//                                 ...acc,
//                                 [node.id]: {
//                                     x: node.position.x - n.position.x,
//                                     y: node.position.y - n.position.y,
//                                 },
//                             }), {}),
//                     };
//                 }}
//                 onNodeDrag={(_, n) => {
//                     const dragState = subtreeDragRef.current;
//
//                     if (!dragState || dragState.rootId !== String(n.id)) return;
//
//                     setNodes(currentNodes => currentNodes.map(node => {
//                         const offset = dragState.offsets[node.id];
//
//                         if (!offset) return node;
//
//                         return {
//                             ...node,
//                             position: {
//                                 x: n.position.x + offset.x,
//                                 y: n.position.y + offset.y,
//                             },
//                         };
//                     }));
//                 }}
//                 onNodeDragStop={(_, n) => {
//                     const dragState = subtreeDragRef.current;
//
//                     const movedNodes = nodes
//                         .filter(node => node.id === String(n.id) || dragState?.offsets?.[node.id])
//                         .map(node => {
//                             const offset = dragState?.offsets?.[node.id];
//
//                             return {
//                                 ...node,
//                                 position: offset
//                                     ? {
//                                         x: n.position.x + offset.x,
//                                         y: n.position.y + offset.y,
//                                     }
//                                     : {
//                                         x: n.position.x,
//                                         y: n.position.y,
//                                     },
//                             };
//                         });
//
//                     subtreeDragRef.current = null;
//
//                     onNodePositionChange?.(
//                         String(n.id),
//                         { x: n.position.x, y: n.position.y },
//                         n.data?.entity,
//                         n.type,
//                         movedNodes
//                     );
//                 }}
//                 onDrop={(e) => {
//                     const type = e.dataTransfer.getData("application/reactflow");
//
//                     if (type) {
//                         onRequestDialog?.({
//                             mode: "create-root",
//                             position: screenToFlowPosition({ x: e.clientX, y: e.clientY }),
//                             draft: { type },
//                         });
//                     }
//                 }}
//                 onDragOver={(e) => e.preventDefault()}
//                 zoomOnDoubleClick={false}>
//                 <Background className="surface-100" />
//                 <Controls />
//             </ReactFlow>
//         </div>
//     );
// }
//
// export default function ZofinoGraph(props) {
//     return (
//         <ReactFlowProvider>
//             <Flow {...props} />
//         </ReactFlowProvider>
//     );
// }
